// Component: useChartData.js
// Purpose: Fetch, page, deduplicate, and repair SmartChart history.

import { useState, useEffect, useCallback, useRef } from 'react';
import moment from 'moment-timezone';
import { services } from '../../services/api';
import WebSocketManager from '../../websocket/WebSocketManager';
import { snapToBar, toUnixSec } from '../../utils/tradeMarkerBuilder';
import { historyFetchWindow } from './historyFetchWindow';

export { historyFetchWindow } from './historyFetchWindow';

const MAX_REPAIR_DAYS = 30;
const MAX_REPAIR_PAGES = 4;

const rowsEqual = (left, right) => {
    if (left === right) return true;
    const leftKeys = Object.keys(left || {});
    const rightKeys = Object.keys(right || {});
    return leftKeys.length === rightKeys.length
        && leftKeys.every((key) => Object.is(left[key], right[key]));
};

//--> Chronological union. Pagination keeps held rows; repair may replace them.
export const mergeOHLCPages = (existing = [], incoming = [], { preferIncoming = false } = {}) => {
    if (!incoming || incoming.length === 0) return existing;
    if (!existing || existing.length === 0) return [...incoming];

    const byTime = new Map();
    existing.forEach((row) => byTime.set(row.time ?? row.timestamp, row));
    incoming.forEach((row) => {
        const key = row.time ?? row.timestamp;
        if (!byTime.has(key) || preferIncoming) byTime.set(key, row);
    });
    const merged = Array.from(byTime.values()).sort((a, b) => {
        const aTime = toUnixSec(a.time ?? a.timestamp) ?? 0;
        const bTime = toUnixSec(b.time ?? b.timestamp) ?? 0;
        return aTime - bTime;
    });
    if (merged.length === existing.length && merged.every((row, index) => rowsEqual(row, existing[index]))) {
        return existing;
    }
    return merged;
};

/*>>> ROLLOVER PRECEDENCE <<<*/
// F5 (MED-7) — single home for the precedence rule that was previously duplicated
// 4x in this file. The instrument object's own rollover_days (if present) WINS
// over the caller-supplied rolloverDays prop — preserved via an `!== undefined`
// check, not truthiness, so an explicit 0 on the instrument still wins.
// LB-8 (F-06) — the gate is now STRUCTURAL, not empirical: when the caller knows
// isContinuous === false, this returns 0 UNCONDITIONALLY, even if a stray
// instrument.rollover_days sneaks onto a PINNED instrument object. Only when
// isContinuous === true does the instrument-vs-prop precedence above apply.
// When isContinuous is undefined (a legacy caller that hasn't been threaded
// yet), today's precedence is preserved as a fallback. F-NEW-1 (QA iter 3)
// corrects the prior overstated claim here: every LIVE, JSX-reachable
// <SmartChart> caller now threads an explicit isContinuous (StrategyBuilder,
// StrategyAnalysisView, LiveAnalysisTab, SmartTrade — the
// last two closed this iteration) — so `undefined` is a genuinely unreachable
// path today, not merely an aspirational one.
export const resolveRolloverDays = (instrument, rolloverDays, isContinuous) => {
    if (isContinuous === false) return 0;
    return instrument?.rollover_days !== undefined ? instrument.rollover_days : rolloverDays;
};

/**
 * useChartData Hook
 * Handles historical data fetching and state management for SmartChart.
 *
 * @param {Object} params
 * @param {Object} params.instrument - Instrument object (must contain instrument_id)
 * @param {string} params.timeframe - '1m', '5m', '1h', '1D'
 * @param {string} params.mode - 'live' | 'backtest' | 'rd'
 * @param {Object} params.dateRange - { start, end } for visual range (zoom)
 * @param {Object} params.fetchDateRange - { start, end } for OHLC fetch range
 * @param {number} params.rolloverDays - Optional continuous FNO stitch overlap padding
 * @param {boolean} params.isContinuous - LB-8 (F-06): structural gate for resolveRolloverDays;
 *        pass the real isContinuous flag so a PINNED instrument can never resolve a nonzero
 *        rollover_days even if the instrument object carries a stray value.
 */
export const useChartData = ({
    instrument,
    timeframe,
    mode,
    dateRange,
    fetchDateRange = null,
    rolloverDays = 0,
    isContinuous,
    resolvedInstrumentId = null,
    onGapRepair = null,
}) => {
    const [data, setData] = useState([]);
    const [rawHistory, setRawHistory] = useState([]); // Cache for unmapped backend data
    const [loading, setLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState("Loading Chart...");
    const [error, setError] = useState(null);

    // --> Pagination state for lazy OHLC loading
    const [paginationState, setPaginationState] = useState({
        currentPage: 1,
        totalDays: 0,
        totalRecords: 0,
        totalPages: 0,
        pageSize: 10,
        hasNext: false,
        hasPrev: false,
        isLoadingPage: false
    });

    // --> Accumulated OHLC history (combines all pages)
    const rawHistoryRef = useRef([]);
    const allLoadedPagesRef = useRef(new Set()); // Track which pages loaded

    // Ref to hold data for access in closures/callbacks without stale state
    const dataRef = useRef([]);
    //--> Request deduplication: track the key for which we have successfully loaded data.
    //    Only set AFTER successful response to avoid blocking retries after abort/error.
    const completedFetchKeyRef = useRef('');
    //--> Track in-flight key to suppress duplicate simultaneous requests (same pattern as useIndicators)
    const inFlightRef = useRef(null);
    //--> AbortController: cancel stale OHLC fetches when timeframe/instrument changes.
    //    Without this, a slow 1m fetch can complete after a fast 5m fetch and overwrite
    //    the chart with wrong-timeframe data (race condition on timeframe switch).
    const abortControllerRef = useRef(null);
    //--> Ref-based guard for loadPage to prevent concurrent page loads.
    //    React state (isLoadingPage) updates asynchronously, so two rapid calls
    //    can both see isLoadingPage=false.  This ref is set synchronously.
    const isLoadingPageRef = useRef(false);
    //--> #50/R6: separate abort controller for foreground gap-repair fetches, so a
    //    repair in flight does not get torn down by (or itself abort) a normal
    //    fetchHistory/loadPage cycle sharing abortControllerRef.
    const foregroundAbortControllerRef = useRef(null);
    const foregroundAbortReasonRef = useRef(null);
    const pendingRepairRef = useRef(null);
    const refetchMissedRangeRef = useRef(null);
    const liveTopicIdRef = useRef(null);
    const onGapRepairRef = useRef(onGapRepair);
    liveTopicIdRef.current = resolvedInstrumentId || instrument?.instrument_id || null;
    onGapRepairRef.current = onGapRepair;

    useEffect(() => {
        dataRef.current = data;
    }, [data]);

    const isHistoryFetchLive = useCallback(() => (
        Boolean(inFlightRef.current && !inFlightRef.current.controller.signal.aborted)
    ), []);

    const fetchHistory = useCallback(async () => {
        if (!instrument?.instrument_id) {
            setLoading(false);
            return;
        }

        const window = historyFetchWindow({ mode, timeframe, fetchDateRange, dateRange });
        if (!window) {
            console.log('[PHASE1-PAGINATION] No fetchRange.start provided, skipping');
            setLoading(false);
            return;
        }
        const { startDate, endDate } = window;
        const rolloverSent = resolveRolloverDays(instrument, rolloverDays, isContinuous);
        const fetchKey = `${instrument.instrument_id}|${timeframe}|${rolloverSent}|${startDate.format('YYYY-MM-DD')}|${endDate.format('YYYY-MM-DD')}`;

        if (completedFetchKeyRef.current === fetchKey) {
            console.log('[DIAG] history fetch skipped', { reason: 'completed', key: fetchKey });
            return;
        }
        if (inFlightRef.current?.key === fetchKey && !inFlightRef.current.controller.signal.aborted) {
            console.log('[DIAG] history fetch skipped', { reason: 'in_flight', key: fetchKey });
            return;
        }

        if (abortControllerRef.current) abortControllerRef.current.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        inFlightRef.current = { key: fetchKey, controller };
        setLoading(true);
        setLoadingMessage('Checking Local Data...');
        setError(null);

        //!--> The completed key describes the base. Invalidate it when the base is reset.
        allLoadedPagesRef.current = new Set();
        rawHistoryRef.current = [];
        completedFetchKeyRef.current = '';

        const perfStart = performance.now();
        try {
            console.log('[PHASE1-PAGINATION] fetchHistory START');
            const pageSizeDays = paginationState.pageSize || 10;
            const totalDays = endDate.diff(startDate, 'days') + 1;
            let currentPageToFetch = Math.max(1, Math.ceil(totalDays / pageSizeDays));
            let fetchedData = [];
            let pagination = null;

            try {
                while (currentPageToFetch >= 1 && fetchedData.length === 0) {
                    const response = await services.marketData.getPagedOHLC({
                        instrument_id: instrument.instrument_id,
                        timeframe,
                        start_date: startDate.format('YYYY-MM-DDTHH:mm:ss'),
                        end_date: endDate.format('YYYY-MM-DDTHH:mm:ss'),
                        page: currentPageToFetch,
                        page_size_days: pageSizeDays,
                        instrument_type: instrument.instrument_type,
                        symbol: instrument.symbol,
                        exchange: instrument.exchange,
                        expiry_date: instrument.expiry_date,
                        rollover_days: rolloverSent,
                    }, { signal: controller.signal });
                    if (controller.signal.aborted) return;
                    const body = response?.data;
                    fetchedData = Array.isArray(body?.data)
                        ? body.data
                        : (Array.isArray(body) ? body : []);
                    pagination = body?.pagination;
                    if (fetchedData.length > 0 || currentPageToFetch === 1) break;
                    currentPageToFetch -= 1;
                }
            } catch (pagedError) {
                if (controller.signal.aborted) throw pagedError;
                console.warn('[PHASE1-PAGINATION] Paginated fetch failed, falling back to full fetch:', pagedError?.message);
                try {
                    const response = await services.marketData.getDateRangeData(
                        instrument.instrument_id,
                        { start_date: startDate.format('YYYY-MM-DD'), end_date: endDate.format('YYYY-MM-DD'), timeframe },
                        { ...instrument, rollover_days: rolloverSent },
                        { signal: controller.signal },
                    );
                    const body = response?.data;
                    fetchedData = Array.isArray(body?.data)
                        ? body.data
                        : (Array.isArray(body?.data?.data) ? body.data.data : []);
                } catch (fallbackError) {
                    const code = String(fallbackError?.code || fallbackError?.status || fallbackError?.message || '').toLowerCase();
                    if (!code.includes('404') && !code.includes('not found')) throw fallbackError;
                    fetchedData = [];
                }
            }

            if (controller.signal.aborted) return;
            if ((mode === 'live' || mode === 'paper') && fetchDateRange?.end) {
                const clipEpoch = toUnixSec(fetchDateRange.end);
                fetchedData = fetchedData.filter((row) => (toUnixSec(row.time ?? row.timestamp) ?? Infinity) <= clipEpoch);
            }

            completedFetchKeyRef.current = fetchKey;
            rawHistoryRef.current = fetchedData;
            setRawHistory(fetchedData);
            if (pagination) {
                setPaginationState((prev) => ({
                    ...prev,
                    currentPage: pagination.current_page || currentPageToFetch,
                    totalDays: pagination.total_days,
                    totalPages: pagination.total_pages,
                    hasNext: pagination.has_next || false,
                    hasPrev: pagination.has_prev || false,
                    isLoadingPage: false,
                }));
                allLoadedPagesRef.current.add(pagination.current_page || currentPageToFetch);
            }
            console.log(`[PHASE1-PAGINATION] ✅ Fetch complete. Took ${(performance.now() - perfStart).toFixed(2)}ms. Fetched ${fetchedData.length} records.`);

            //! Release the owner before replay so the repair passes its entry gate.
            if (inFlightRef.current?.controller === controller) {
                inFlightRef.current = null;
                setLoading(false);
                const pending = pendingRepairRef.current;
                pendingRepairRef.current = null;
                if (pending) {
                    console.log('[DIAG] foreground repair resumed', pending);
                    refetchMissedRangeRef.current?.(pending);
                }
            }
        } catch (err) {
            if (err?.name === 'AbortError' || err?.message === 'canceled' || controller.signal.aborted) return;
            console.error('[PHASE1-PAGINATION] History fetch failed', err);
            setError(err?.message || 'Failed to load historical data');
            setRawHistory([]);
            if (pendingRepairRef.current) {
                pendingRepairRef.current = null;
                console.log('[DIAG] foreground repair dropped', { reason: 'history_fetch_failed' });
            }
        } finally {
            if (inFlightRef.current?.controller === controller) {
                inFlightRef.current = null;
                setLoading(false);
            }
        }
    }, [instrument, timeframe, mode, rolloverDays, isContinuous, paginationState.pageSize, JSON.stringify(fetchDateRange || dateRange)]); // eslint-disable-line react-hooks/exhaustive-deps

    // --> Load a specific page of OHLC data
    const loadPage = useCallback(async (pageNum) => {
        console.log(`[PHASE1-PAGINATION] loadPage(${pageNum}) called`);
        //--> Use ref-based guard (synchronous) to prevent concurrent loads.
        //    React state isLoadingPage updates asynchronously, so two rapid calls
        //    (e.g. double-click on period analysis) can both pass the state check.
        if (isLoadingPageRef.current || paginationState.isLoadingPage || isHistoryFetchLive() || !instrument?.instrument_id || !timeframe) {
            console.log(`[PHASE1-PAGINATION]   Early exit: isLoadingRef=${isLoadingPageRef.current}, isLoading=${paginationState.isLoadingPage}, historyFetchLive=${isHistoryFetchLive()}, instrumentId=${instrument?.instrument_id}, timeframe=${timeframe}`);
            return null;
        }

        // --> Already loaded this page, update pagination state and return from cache
        if (allLoadedPagesRef.current.has(pageNum)) {
            console.log(`[PHASE1-PAGINATION]   Page ${pageNum} already loaded, returning from cache. Total records: ${rawHistoryRef.current.length}`);
            //--> Still update currentPage so scroll detection uses correct hasNext/hasPrev
            setPaginationState(prev => ({
                ...prev,
                currentPage: pageNum,
                hasNext: prev.totalPages ? pageNum < prev.totalPages : false,
                hasPrev: pageNum > 1,
            }));
            return rawHistoryRef.current;
        }

        console.log(`[PHASE1-PAGINATION]   Fetching page ${pageNum}...`);
        isLoadingPageRef.current = true;
        setPaginationState(prev => ({ ...prev, isLoadingPage: true }));

        try {
            const window = historyFetchWindow({ mode, timeframe, fetchDateRange, dateRange });
            if (!window) return null;
            const { startDate, endDate } = window;

            const response = await services.marketData.getPagedOHLC({
                instrument_id: instrument.instrument_id,
                timeframe,
                start_date: startDate.format('YYYY-MM-DDTHH:mm:ss'),
                end_date: endDate.format('YYYY-MM-DDTHH:mm:ss'),
                page: pageNum,
                page_size_days: paginationState.pageSize,
                // Forward instrument metadata for rollover stitching
                instrument_type: instrument.instrument_type,
                symbol: instrument.symbol,
                exchange: instrument.exchange,
                expiry_date: instrument.expiry_date,
                rollover_days: resolveRolloverDays(instrument, rolloverDays, isContinuous),
            }, { signal: abortControllerRef.current?.signal });

            //--> Unwrap axios response: response.data = JSON body
            const body = response?.data;
            console.log(`[PHASE1-PAGINATION]   loadPage response status: ${body?.status}, records: ${body?.data?.length}`);

            if (body?.status !== 'success') {
                throw new Error(body?.error?.message || 'Failed to load page');
            }

            // --> Merge new page into rawHistory, maintaining chronological order
            const newOHLC = body?.data || [];
            const merged = mergeOHLCPages(rawHistoryRef.current, newOHLC);
            rawHistoryRef.current = merged;
            setRawHistory(merged);
            allLoadedPagesRef.current.add(pageNum);

            const newPaginationState = {
                currentPage: pageNum,
                totalDays: body?.pagination?.total_days,
                totalPages: body?.pagination?.total_pages,
                hasNext: body?.pagination?.has_next || false,
                hasPrev: body?.pagination?.has_prev || false,
                isLoadingPage: false
            };

            isLoadingPageRef.current = false;
            setPaginationState(prev => ({ ...prev, ...newPaginationState }));

            console.log(`[PHASE1-PAGINATION] ✅ Loaded page ${pageNum}. ${newOHLC.length} new records. Total cumulative: ${rawHistoryRef.current.length}`);
            console.log(`[PHASE1-PAGINATION]   Pagination state: currentPage=${newPaginationState.currentPage}, totalPages=${newPaginationState.totalPages}, hasNext=${newPaginationState.hasNext}, hasPrev=${newPaginationState.hasPrev}`);

            return rawHistoryRef.current;
        } catch (error) {
            isLoadingPageRef.current = false;
            console.warn('[PHASE1-PAGINATION] Failed to load page:', error?.message || error);
            setPaginationState(prev => ({
                ...prev,
                isLoadingPage: false,
                //--\> If we failed going backward, mark no more pages to prevent infinite retry
                ...(pageNum < prev.currentPage ? { hasPrev: false } : {}),
                ...(pageNum > prev.currentPage ? { hasNext: false } : {}),
            }));
            //--\> Don't set global error — visible data is already loaded.
            //    setError here would cause SmartChart to display "Unable to load data"
            //    even though the current page is rendering fine.
            return rawHistoryRef.current;
        }
    }, [instrument, timeframe, mode, paginationState.isLoadingPage, paginationState.pageSize, fetchDateRange, dateRange, isContinuous, rolloverDays, isHistoryFetchLive]);

    // --> Load next page callback
    const loadNextPage = useCallback(() => {
        console.log(`[PHASE1-PAGINATION] loadNextPage called. hasNext=${paginationState.hasNext}, currentPage=${paginationState.currentPage}`);
        if (paginationState.hasNext) {
            console.log(`[PHASE1-PAGINATION]   Loading page ${paginationState.currentPage + 1}`);
            return loadPage(paginationState.currentPage + 1);
        }
        console.log(`[PHASE1-PAGINATION]   Cannot load next page (hasNext=false)`);
        return null;
    }, [paginationState.hasNext, paginationState.currentPage, loadPage]);

    // --> Load previous page callback
    const loadPreviousPage = useCallback(() => {
        console.log(`[PHASE1-PAGINATION] loadPreviousPage called. hasPrev=${paginationState.hasPrev}, currentPage=${paginationState.currentPage}`);
        if (paginationState.hasPrev) {
            console.log(`[PHASE1-PAGINATION]   Loading page ${paginationState.currentPage - 1}`);
            return loadPage(paginationState.currentPage - 1);
        }
        console.log(`[PHASE1-PAGINATION]   Cannot load previous page (hasPrev=false)`);
        return null;
    }, [paginationState.hasPrev, paginationState.currentPage, loadPage]);

    /*>>> FOREGROUND GAP REPAIR (#50 / R6) <<<*/
    //--> Own-topic live/paper charts are the sole consumer of `tab_foregrounded`
    //    (emitted by WebSocketManager.markForegrounded(), owned by
    //    WebSocketProvider). WS is live-only with no replay, so any real socket
    //    death while backgrounded left a permanent hole with the tab resuming
    //    silently from `now`; this refetches exactly the missed window instead.
    const refetchMissedRange = useCallback(async ({ backgroundDuration = 0, eventName = 'unknown', trigger = 'duration' } = {}) => {
        if (!instrument?.instrument_id || !timeframe) {
            console.log('[DIAG] chart gap repair skipped', { event: eventName, backgroundDuration, ownTopic: null, topicsCount: 0, reason: 'no_instrument_or_timeframe' });
            return;
        }
        const event = { backgroundDuration, eventName, trigger };
        if (isHistoryFetchLive()) {
            const previous = pendingRepairRef.current;
            pendingRepairRef.current = {
                ...event,
                backgroundDuration: Math.max(backgroundDuration, previous?.backgroundDuration || 0),
            };
            console.log('[DIAG] foreground repair deferred', { ...pendingRepairRef.current, at: 'entry', reason: 'history_fetch_in_flight' });
            return;
        }

        if (foregroundAbortControllerRef.current && !foregroundAbortControllerRef.current.signal.aborted) {
            foregroundAbortReasonRef.current = 'superseded';
            foregroundAbortControllerRef.current.__repairAbortReason = 'superseded';
            foregroundAbortControllerRef.current.abort();
        }
        const controller = new AbortController();
        foregroundAbortControllerRef.current = controller;
        foregroundAbortReasonRef.current = null;

        const nowMs = Date.now();
        const endDate = moment.utc(nowMs);
        const persistedLast = rawHistoryRef.current[rawHistoryRef.current.length - 1];
        const persistedSec = toUnixSec(persistedLast?.time ?? persistedLast?.timestamp);
        let startMs = persistedSec && persistedSec > 1e9
            ? persistedSec * 1000
            : nowMs - Math.max(0, backgroundDuration);
        const earliestMs = nowMs - MAX_REPAIR_DAYS * 86400 * 1000;
        const clamped = startMs < earliestMs;
        if (clamped) startMs = earliestMs;
        const startDate = moment.utc(startMs);
        const pageSizeDays = paginationState.pageSize || 10;
        const liveTopic = liveTopicIdRef.current ? `market.${liveTopicIdRef.current}.tick` : null;
        const requestPage = (page) => services.marketData.getPagedOHLC({
            instrument_id: instrument.instrument_id,
            timeframe,
            start_date: startDate.format('YYYY-MM-DDTHH:mm:ss'),
            end_date: endDate.format('YYYY-MM-DDTHH:mm:ss'),
            page,
            page_size_days: pageSizeDays,
            instrument_type: instrument.instrument_type,
            symbol: instrument.symbol,
            exchange: instrument.exchange,
            expiry_date: instrument.expiry_date,
            rollover_days: resolveRolloverDays(instrument, rolloverDays, isContinuous),
        }, { signal: controller.signal });

        console.log('[DIAG] foreground repair: refetching missed range', {
            event: eventName,
            trigger,
            instrument_id: instrument.instrument_id,
            liveTopic,
            start: startDate.toISOString(),
            end: endDate.toISOString(),
            pages: 'server',
            clamped,
        });

        try {
            const first = await requestPage(1);
            const totalPages = Math.max(1, Number(first?.data?.pagination?.total_pages) || 1);
            let pageNumbers = [];
            if (totalPages > 1) {
                if (totalPages <= MAX_REPAIR_PAGES) {
                    pageNumbers = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
                } else {
                    const firstNewest = totalPages - MAX_REPAIR_PAGES + 2;
                    pageNumbers = Array.from({ length: MAX_REPAIR_PAGES - 1 }, (_, index) => firstNewest + index);
                    console.log('[DIAG] foreground repair page cap', {
                        total_pages: totalPages,
                        fetched: [1, ...pageNumbers],
                        skipped: `2..${firstNewest - 1}`,
                    });
                }
            }
            const rest = await Promise.all(pageNumbers.map(requestPage));
            if (controller.signal.aborted) {
                console.log('[DIAG] foreground repair aborted', { reason: controller.__repairAbortReason || foregroundAbortReasonRef.current || 'series_change_or_unmount', event: eventName });
                return;
            }
            if (isHistoryFetchLive()) {
                const previous = pendingRepairRef.current;
                pendingRepairRef.current = {
                    ...event,
                    backgroundDuration: Math.max(backgroundDuration, previous?.backgroundDuration || 0),
                };
                console.log('[DIAG] foreground repair deferred', { ...pendingRepairRef.current, at: 'land', reason: 'history_fetch_in_flight' });
                return;
            }

            const before = rawHistoryRef.current;
            let merged = before;
            const beforeByTime = new Map(before.map((row) => [row.time ?? row.timestamp, row]));
            const replacedKeys = new Set();
            [first, ...rest].forEach((response) => {
                const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
                rows.forEach((row) => {
                    const key = row.time ?? row.timestamp;
                    const held = beforeByTime.get(key);
                    if (held && !rowsEqual(held, row)) replacedKeys.add(key);
                });
                merged = mergeOHLCPages(merged, rows, { preferIncoming: true });
            });
            rawHistoryRef.current = merged;
            if (merged !== before) setRawHistory(merged);
            onGapRepairRef.current?.({ start: startDate.toISOString(), end: endDate.toISOString(), trigger: eventName });
            console.log('[DIAG] foreground repair complete', {
                instrument_id: instrument.instrument_id,
                liveTopic,
                totalRecords: merged.length,
                added: Math.max(0, merged.length - before.length),
                replaced: replacedKeys.size,
                identityKept: merged === before,
            });
        } catch (err) {
            if (err?.name === 'AbortError' || err?.message === 'canceled' || controller.signal.aborted) {
                console.log('[DIAG] foreground repair aborted', { reason: controller.__repairAbortReason || foregroundAbortReasonRef.current || 'series_change_or_unmount', event: eventName });
                return;
            }
            console.warn('[DIAG] foreground repair failed', err?.message || err);
        }
    }, [instrument, timeframe, rolloverDays, isContinuous, paginationState.pageSize, isHistoryFetchLive]);
    refetchMissedRangeRef.current = refetchMissedRange;

    useEffect(() => {
        //--> Backtest/rd render a fixed historical window and cannot go stale on
        //    backgrounding — only live/paper charts stream and can lose data.
        if (mode !== 'live' && mode !== 'paper') return undefined;

        //--> #50-1: ONE shared gap-repair gate for both event sources — a long tab
        //    background (`tab_foregrounded`) and a socket drop/reconnect while the
        //    tab stayed foregrounded (`ws_reconnected_after_gap`, e.g. a backend
        //    restart). Both events carry the same {backgroundDuration, topics}
        //    shape by design (WebSocketManager.markForegrounded / handleAuthSuccess)
        //    so this stays a single code path into refetchMissedRange — no second
        //    mechanism per instance.
        const handleGap = (minDurationMs, eventName) => ({ backgroundDuration = 0, topics } = {}) => {
            const liveTopicId = liveTopicIdRef.current;
            const ownTopic = liveTopicId ? `market.${liveTopicId}.tick` : null;
            const baseDiag = { event: eventName, backgroundDuration, ownTopic, topicsCount: Array.isArray(topics) ? topics.length : 0 };
            if (!instrument?.instrument_id) {
                console.log('[DIAG] chart gap repair skipped', { ...baseDiag, reason: 'no_instrument' });
                return;
            }
            if (!Array.isArray(topics) || topics.length === 0) {
                console.log('[DIAG] chart gap repair skipped', { ...baseDiag, reason: 'no_topics' });
                return;
            }
            if (!ownTopic || !topics.includes(ownTopic)) {
                console.log('[DIAG] chart gap repair skipped', { ...baseDiag, reason: 'own_topic_absent' });
                return;
            }

            const held = dataRef.current[dataRef.current.length - 1];
            const heldLast = toUnixSec(held?.time ?? held?.timestamp);
            const cursor = heldLast == null ? 'none' : (Number.isFinite(heldLast) && heldLast > 1e9 ? heldLast : 'unparseable');
            const nowSec = Math.floor(Date.now() / 1000);
            const currentBucket = snapToBar(nowSec, timeframe);
            const lastClosed = currentBucket == null ? null : snapToBar(currentBucket - 1, timeframe);
            if (typeof cursor === 'number' && lastClosed > cursor) {
                refetchMissedRange({ backgroundDuration, eventName, trigger: 'cursor' });
                return;
            }
            if (backgroundDuration > minDurationMs) {
                refetchMissedRange({ backgroundDuration, eventName, trigger: 'duration' });
                return;
            }
            console.log('[DIAG] chart gap repair skipped', { ...baseDiag, cursor, reason: 'under_threshold' });
        };
        //--> Tab-background gate stays 60s (Chrome throttles a hidden tab hard
        //    enough that a shorter gap is noise, not a real hole).
        const handleForegrounded = handleGap(60000, 'tab_foregrounded');
        //--> Socket-drop gate is much lower: a dropped/reconnecting transport has
        //    NO other repair path (unlike backgrounding, which at least still
        //    delivers throttled frames) — even a 5s backend-restart blip leaves a
        //    real hole with nothing else to fill it.
        const handleReconnectedAfterGap = handleGap(5000, 'ws_reconnected_after_gap');

        WebSocketManager.on('tab_foregrounded', handleForegrounded);
        WebSocketManager.on('ws_reconnected_after_gap', handleReconnectedAfterGap);

        return () => {
            WebSocketManager.off('tab_foregrounded', handleForegrounded);
            WebSocketManager.off('ws_reconnected_after_gap', handleReconnectedAfterGap);
            if (foregroundAbortControllerRef.current) {
                foregroundAbortReasonRef.current = 'series_change_or_unmount';
                foregroundAbortControllerRef.current.__repairAbortReason = 'series_change_or_unmount';
                foregroundAbortControllerRef.current.abort();
            }
            pendingRepairRef.current = null;
        };
    }, [mode, instrument?.instrument_id, refetchMissedRange]);

    // Trigger fetch on dependency change
    useEffect(() => {
        fetchHistory();
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [fetchHistory]);

    return {
        data,
        setData,
        rawHistory,
        setRawHistory, // Exposed if manual manipulation needed
        loading,
        setLoading,    // Exposed for manual download triggers
        loadingMessage,
        setLoadingMessage,
        error,
        setError,
        fetchHistory,
        dataRef,
        // --> Pagination state and callbacks
        paginationState,
        loadNextPage,
        loadPreviousPage,
        loadPage
    };
};
